import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

// Import all components
import AuditProgressDashboard from './components/AuditProgressDashboard';
import AuditQueue from './components/AuditQueue';
import AuditTrailPanel from './components/AuditTrailPanel';
import ExceptionManagement from './components/ExceptionManagement';
import QuickActionToolbar from './components/QuickActionToolbar';
import AuditFilters from './components/AuditFilters';

const QuarterlyAuditWorkflow = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [filters, setFilters] = useState({});

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleBulkVerify = (selectedAssets) => {
    console.log('Bulk verifying assets:', selectedAssets);
    // Simulate bulk verification process
    alert(`Initiating bulk verification for ${selectedAssets?.length} assets`);
  };

  const handlePrintSheets = () => {
    console.log('Printing audit sheets');
    alert('Generating printable audit verification sheets...');
  };

  const handleExportReports = () => {
    console.log('Exporting audit reports');
    alert('Exporting audit data and verification reports...');
  };

  const handleGenerateCompliance = () => {
    console.log('Generating compliance documentation');
    alert('Generating SOX compliance documentation...');
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    console.log('Filters updated:', newFilters);
  };

  const viewTabs = [
    { key: 'dashboard', label: 'Progress Dashboard', icon: 'BarChart3' },
    { key: 'queue', label: 'Audit Queue', icon: 'List' },
    { key: 'trail', label: 'Audit Trail', icon: 'History' },
    { key: 'exceptions', label: 'Exceptions', icon: 'AlertTriangle' }
  ];

  const renderActiveView = () => {
    switch (activeView) {
      case 'dashboard':
        return <AuditProgressDashboard />;
      case 'queue':
        return <AuditQueue onBulkVerify={handleBulkVerify} />;
      case 'trail':
        return <AuditTrailPanel />;
      case 'exceptions':
        return <ExceptionManagement />;
      default:
        return <AuditProgressDashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={handleToggleSidebar} 
      />
      <main className={`pt-16 transition-layout ${
        sidebarCollapsed ? 'ml-16' : 'ml-72'
      }`}>
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div>
              <Breadcrumb />
              <div className="flex items-center space-x-3 mt-2">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="ClipboardCheck" size={24} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Quarterly Audit Workflow</h1>
                  <p className="text-muted-foreground">
                    Q3 2025 Asset Verification & Compliance Management
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right text-sm">
                <div className="text-foreground font-medium">Current Quarter: Q3 2025</div>
                <div className="text-muted-foreground">Audit Period: Jul 1 - Sep 30, 2025</div>
              </div>
              <Button variant="default">
                <Icon name="FileCheck" size={16} className="mr-2" />
                Generate Final Report
              </Button>
            </div>
          </div>

          {/* Quick Action Toolbar */}
          <QuickActionToolbar
            onPrintSheets={handlePrintSheets}
            onExportReports={handleExportReports}
            onGenerateCompliance={handleGenerateCompliance}
          />

          {/* Audit Filters */}
          <AuditFilters onFiltersChange={handleFiltersChange} />

          {/* View Tabs */}
          <div className="bg-card border border-border rounded-lg shadow-subtle">
            <div className="border-b border-border">
              <nav className="flex space-x-1 p-1">
                {viewTabs?.map((tab) => (
                  <button
                    key={tab?.key}
                    onClick={() => setActiveView(tab?.key)}
                    className={`flex items-center space-x-2 px-4 py-3 rounded-md text-sm font-medium transition-smooth ${
                      activeView === tab?.key
                        ? 'bg-primary text-primary-foreground shadow-subtle'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
            
            <div className="p-6">
              {renderActiveView()}
            </div>
          </div>

          {/* Footer with Audit Instructions */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Info" size={20} color="white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-card-foreground mb-2">
                  Audit Instructions & Guidance
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
                  <div>
                    <h4 className="font-medium text-card-foreground mb-2">Verification Process</h4>
                    <ul className="space-y-1 text-muted-foreground">
                      <li>• Physically locate each asset</li>
                      <li>• Scan barcode or verify Asset ID</li>
                      <li>• Confirm location matches records</li>
                      <li>• Update status and condition notes</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-card-foreground mb-2">Exception Handling</h4>
                    <ul className="space-y-1 text-muted-foreground">
                      <li>• Report missing assets immediately</li>
                      <li>• Document location discrepancies</li>
                      <li>• Escalate unresolved issues</li>
                      <li>• Maintain detailed audit trail</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-card-foreground mb-2">Compliance Requirements</h4>
                    <ul className="space-y-1 text-muted-foreground">
                      <li>• Complete verification within 30 days</li>
                      <li>• Maintain SOX documentation standards</li>
                      <li>• Ensure proper authorization for changes</li>
                      <li>• Submit final report by quarter end</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-md">
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={16} className="text-warning" />
                    <span className="text-sm font-medium text-warning">
                      Audit Deadline: September 30, 2025 (23 days remaining)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default QuarterlyAuditWorkflow;